<template>
  <div class="map-view content-layout">
    <button
      class="btn btn-primary list-side-menu-toggler"
      :class="{ expand: sideMenuExpanded }"
      @click="() => (sideMenuExpanded = !sideMenuExpanded)"
    >
      <font-awesome-icon :icon="['fas', sideMenuExpanded ? 'times' : 'bars']" />
    </button>
    <div
      class="list-side-menu"
      :class="{ expand: sideMenuExpanded, 'overflow-hidden': isLoadingOptions }"
    >
      <loading-spinner
        class="loading-spinner-component"
        style="position: fixed; max-width: 300px; left: 0px; top: 70px"
        v-if="isLoadingOptions"
      />
      <div class="form-group">
        <label class="text-primary">
          <font-awesome-icon
            :icon="['fas', 'map-marker-alt']"
            class="mr-2 text-muted"
          />District
        </label>
        <v-select
          :clearable="false"
          placeholder="Select a district"
          class="report-filter"
          v-model="models.district"
          :options="options.district"
          label="name"
          @input="optionChanged('district')"
          :disabled="user && user.district.length > 0"
        >
          <i slot="spinner" class="icon icon-spinner"></i>
        </v-select>
      </div>
      <div class="form-group">
        <label class="text-primary">
          <font-awesome-icon
            :icon="['fas', 'bolt']"
            class="mr-2 text-muted"
          />Source
        </label>
        <v-select
          :clearable="false"
          placeholder="Select a source"
          class="report-filter"
          v-model="models.source"
          :disabled="!models.district"
          :options="
            options.source.filter(
              (s) => models.district && s.place == models.district._id
            )
          "
          @input="optionChanged('source')"
          label="name"
        ></v-select>
      </div>
      <div class="form-group">
        <label class="text-primary">
          <font-awesome-icon
            :icon="['fas', 'bolt']"
            class="mr-2 text-muted"
          />Category
        </label>
        <v-select
          :clearable="false"
          placeholder="Select a category"
          class="report-filter"
          v-model="models.category"
          :disabled="!models.source"
          :options="options.category"
          @input="optionChanged('category')"
          label="name"
        ></v-select>
      </div>
      <div class="form-group">
        <label class="text-primary">
          <font-awesome-icon
            :icon="['fas', 'bolt']"
            class="mr-2 text-muted"
          />Feeder
        </label>
        <v-select
          :clearable="false"
          placeholder="Select a feeder"
          class="report-filter"
          v-model="models.feeder"
          :disabled="!models.category"
          :options="
            options.feeder.filter(
              (f) =>
                models.category &&
                f.category == models.category.name &&
                f.place == models.source._id
            )
          "
          @input="optionChanged('feeder')"
          label="name"
        ></v-select>
      </div>
      <div class="form-group">
        <label class="text-primary">
          <font-awesome-icon
            :icon="['fas', 'calendar-alt']"
            class="mr-2 text-muted"
          />Inspection
        </label>
        <v-select
          :clearable="false"
          placeholder="Select inspection month"
          class="report-filter"
          :options="
            options.inspection.filter(
              (i) => models.feeder && i.place == models.feeder._id
            )
          "
          v-model="models.inspection"
          :disabled="!models.feeder"
          @input="fetchMarkers()"
          label="name"
        ></v-select>
      </div>
    </div>
    <div class="list-main-content position-relative p-0">
      <div id="map" class="h-100 w-100 overflow-hidden"></div>
      <map-pin
        v-for="(pole, index) in poles"
        :key="pole.poleId"
        :pole="pole"
        :map="map"
        :index="index"
        :total="poles.length"
      />
      <div
        class="small map-legend px-4 py-2 bg-white border rounded-lg shadow"
        style="position: absolute; bottom: 3px; left: 3px"
      >
        <span>
          <font-awesome-icon
            :icon="['fas', 'circle']"
            class="mr-2 text-normal"
          />
          ({{ countPoleByStatus("normal") }}) Normal
        </span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'circle']"
            class="mr-2 ml-3 text-low"
          />
          ({{ countPoleByStatus("low") }}) Low
        </span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'circle']"
            class="mr-2 ml-3 text-minor"
          />
          ({{ countPoleByStatus("minor") }}) Minor
        </span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'circle']"
            class="mr-2 ml-3 text-critical"
          />
          ({{ countPoleByStatus("critical") }}) Critical
        </span>
        <span class="border-primary border-left border-right ml-2"></span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'wrench']"
            class="mr-2 ml-3 text-muted"
          />
          Pending Maintenance
        </span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'wrench']"
            class="mr-2 ml-3 text-warning"
          />
          Ongoing Maintenance
        </span>
        <span>
          <font-awesome-icon
            :icon="['fas', 'wrench']"
            class="mr-2 ml-3 text-success"
          />
          Maintained
        </span>
      </div>
      <div
        class="map-summary bg-white border rounded-lg shadow d-flex flex-column"
        style="position: absolute; top: 3px; right: 3px"
      >
        <div class="text-center border-bottom p-3">
          <h4 class="mb-0">{{ poles.length }}</h4>
          <p class="mb-0 text-muted small font-weight-bold">POLES</p>
        </div>
        <div class="text-center border-top border-bottom p-3">
          <h4 class="mb-0">{{ defects.length }}</h4>
          <p class="mb-0 text-muted small font-weight-bold">DEFECTS</p>
        </div>
        <div class="border-top text-center bg-light">
          <div class="py-3 mx-3">
            <h5 class="text-primary mb-0">
              {{ defects.filter((d) => d == "visual").length }}
            </h5>
            <p class="small mb-0 text-muted font-weight-bold">VISUAL</p>
          </div>
        </div>
        <div class="text-center bg-light">
          <div class="border-top py-3 mx-3">
            <h5 class="text-primary mb-0">
              {{ defects.filter((d) => d == "thermal").length }}
            </h5>
            <p class="small mb-0 text-muted font-weight-bold">THERMAL</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl/dist/mapbox-gl.js";
import API from "@/api.js";
import moment from "moment";
import LoadingSpinner from "@/components/LoadingSpinner";
import MapPin from "@/components/MapPin";

export default {
  name: "mapView",
  components: {
    "loading-spinner": LoadingSpinner,
    MapPin,
  },
  data() {
    return {
      map: null,
      sideMenuExpanded: false,
      isLoadingOptions: false,
      options: {
        district: [],
        source: [],
        category: [],
        feeder: [],
        inspection: [],
      },
      models: {
        district: null,
        source: null,
        category: null,
        feeder: null,
        inspection: null,
      },
      poles: [],
      defects: [],
      user: JSON.parse(localStorage.getItem("user")),
    };
  },
  watch: {
    models: {
      handler() {
        let item = Object.keys(this.models)
          .filter((x) => x != "pole")
          .reduce((obj, key) => {
            obj[key] = this.models[key];
            return obj;
          }, {});
        localStorage.setItem("models", JSON.stringify(item));
      },
      deep: true,
    },
  },
  methods: {
    countPoleByStatus(status) {
      let count;
      if (status == "minor") {
      }
      return status == "minor"
        ? this.poles.filter(
            (p) =>
              p.status.toLowerCase() == "minor" ||
              p.status.toLowerCase() == "coutious"
          ).length
        : this.poles.filter((p) => p.status.toLowerCase() == status).length;
    },
    async initMap() {
      return new Promise(async (resolve, reject) => {
        this.$emit("isLoading", true);
        try {
          mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_KEY;

          this.map = new mapboxgl.Map({
            container: "map",
            style: "mapbox://styles/mapbox/streets-v11",
            center: [117.001697, 5.388943],
            zoom: 7,
          });

          this.map.on("load", () => {
            this.$emit("isLoading", false);
            resolve();
          });
        } catch (error) {
          console.error(error);
          this.$emit("isLoading", false);
          reject();
        }
      });
    },
    async fetchMarkers() {
      this.$emit("isLoading", true);
      this.sideMenuExpanded = false;
      this.poles = [];

      let url = `report/${this.models.inspection._id}`;

      API.get(url)
        .then((retVal) => {
          const data = retVal.data;

          let poles = data.map((d) => {
            return {
              poleId: d._id,
              pole: d.name,
              lat: d.lat,
              lng: d.lng,
              equipment: d.equipment,
              status: d.status,
              defects: d.defects.length,
              defectsMaintained: d.defects
                .filter((defect) => defect.inMaintenance)
                .map((defect) => {
                  return {
                    _id: defect.id,
                    userUpload: defect.userUpload,
                    userApprove: defect.userApprove,
                  };
                }),
            };
          });

          poles = poles.sort((a, b) => {
            let aNum = parseInt(a.pole.split(" ")[0]);
            let bNum = parseInt(b.pole.split(" ")[0]);

            return aNum - bNum;
          });

          if (poles.length > 0) {
            this.map.flyTo({
              center: [poles[0].lng, poles[0].lat],
              zoom: 17,
              speed: 1,
            });

            this.poles = poles;

            this.defects = data
              .map((p) => p.defects)
              .flat()
              .map((d) => d.type);
          } else {
            this.poles = [];
            this.defects = [];
          }

          this.$emit("isLoading", false);
        })
        .catch((err) => {
          console.error(err);
          this.$emit("isLoading", false);
        });
    },
    getEquipmentName(equipment) {
      let equipmentList = [];

      equipment.forEach((eq) => {
        equipmentList.push(eq.text.split("-")[0].trim());
      });

      if (equipmentList.length > 0) {
        return equipmentList;
      } else {
        return null;
      }
    },
    async fetchOptions() {
      return new Promise((resolve, reject) => {
        this.isLoadingOptions = true;

        API.get("options/sidebar")
          .then((retVal) => {
            this.options = retVal.data;

            this.isLoadingOptions = false;

            resolve();
          })
          .catch((err) => {
            console.error(err);

            this.isLoadingOptions = false;

            reject();
          });
      });
    },
    optionChanged(type) {
      this.poleReports = [];
      this.rawPoleReports = [];

      let typeIndex = Object.keys(this.options).findIndex((key) => key == type);

      Object.keys(this.options).forEach((key, index) => {
        if (index > typeIndex) {
          this.models[key] = null;
        }
      });
    },
  },
  async mounted() {
    this.initMap().then(async (res) => {
      await this.fetchOptions();

      if (this.user && this.user.district) {
        this.models.district = this.options.district.find(
          (d) => d.name.toLowerCase() == this.user.district.toLowerCase()
        );
      }

      const ls = localStorage.getItem("models");

      if (ls) {
        const models = JSON.parse(ls);

        Object.keys(models).forEach((key) => {
          if (models[key]) {
            this.models[key] = models[key];

            if (key == "inspection") {
              this.fetchMarkers();
            }
          }
        });
      }
    });
  },
};
</script>
